/* ============================================================================
   velocity.collection-v2.js — AFX Velocity v2 CRO card behaviours.

   Loaded as type="module" defer by static-collection.liquid when card_style ==
   velocity. Namespaced under .afxv-grid via event delegation so nothing global
   leaks and zero-card collections are a no-op. Three behaviours:
     1. qty stepper (delegated, ± and manual entry, 1..99)
     2. dense-view toggle (swaps the grid density class; persists the choice)
     3. AJAX add → POST /cart/add.js, then GET /cart.js, then dispatch the EXACT
        event the Empire header listens for: window 'cartcount:update' with the
        cart payload in detail. Degrades gracefully if anything fails.

   Cart contract: quantity POSTed = the stepper value. The product/variant IS the
   case (price = case price), so qty is NEVER multiplied by pack. `data-pack` is
   used only to report qty × pack UNITS in the success toast/aria.

   cwv-6: every delegated document listener is registered against an
   AbortController.signal and torn down on shopify:section:unload, then re-bound on
   shopify:section:load — so the theme editor's remove/re-add cycle can't leave a
   stale capture-phase handler double-firing against Empire's own listeners.
   ========================================================================== */
(() => {
  'use strict';
  if (window.__afxvCollectionV2) return;
  window.__afxvCollectionV2 = true;

  const root = () => window.Shopify && window.Shopify.routes && window.Shopify.routes.root
    ? window.Shopify.routes.root
    : '/';
  const CART_ADD = () => `${root()}cart/add.js`;
  const CART_GET = () => `${root()}cart.js`;
  const VIEW_KEY = 'afxv_collection_view';
  const FILTERS_KEY = 'afxv_filters_collapsed';
  const prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── helpers ──────────────────────────────────────────────────────────── */
  function clampQty(n) {
    n = parseInt(n, 10);
    if (!n || n < 1) n = 1;
    if (n > 99) n = 99;
    return n;
  }
  function stepperOf(el) { return el.closest('.afxv-stepper'); }
  function cardOf(el) { return el.closest('[data-afxv-card]'); }

  function reflectQty(stepper) {
    if (!stepper) return;
    const input = stepper.querySelector('[data-afxv-qty]');
    if (!input) return;
    const v = clampQty(input.value);
    input.value = v;
    const dec = stepper.querySelector('.afxv-dec');
    if (dec) dec.disabled = v <= 1;
    const card = cardOf(stepper);
    const lab = card && card.querySelector('[data-afxv-qtylab]');
    if (lab) lab.textContent = v > 1 ? `×${v}` : '';
  }

  /* a11y-14: announce cart state to AT via the section's polite live region. */
  function announce(msg) {
    const sr = document.getElementById('afxv-cart-status');
    if (sr) sr.textContent = msg || '';
  }

  /* ── toast (reused from any existing one, else a lightweight scoped node) ─ */
  let toastNode = null;
  let toastTimer = null;
  function ensureToast() {
    if (toastNode) return toastNode;
    toastNode = document.createElement('div');
    toastNode.className = 'afxv-toast';
    toastNode.setAttribute('role', 'status');
    toastNode.setAttribute('aria-live', 'polite');
    toastNode.style.cssText = [
      'position:fixed', 'left:50%', 'bottom:24px', 'transform:translate(-50%,140%)',
      'z-index:9999', 'background-color:var(--ink,#16110F)', 'color:var(--bg,#FAF6EF)',
      'padding:12px 16px', 'border-radius:var(--radius-md,12px)', 'box-shadow:var(--shadow-e3)',
      'font-family:var(--font-body,sans-serif)', 'font-size:14px', 'font-weight:600',
      'max-width:90vw', 'transition:transform var(--motion-base,320ms) var(--ease-out,ease)'
    ].join(';');
    document.body.appendChild(toastNode);
    return toastNode;
  }
  function showToast(msg) {
    const n = ensureToast();
    n.textContent = msg;
    if (prefersReduced) {
      // skip the slide-in/out animation; appear and disappear in place
      n.style.transition = 'none';
      n.style.transform = 'translate(-50%,0)';
    } else {
      // force a reflow so the transition runs from the hidden state, then reveal
      void n.offsetWidth;
      n.style.transform = 'translate(-50%,0)';
    }
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { n.style.transform = 'translate(-50%,140%)'; }, 3200);
  }

  /* ── notify the Empire header + drawer of the new cart ───────────────────
     Empire dispatches CustomEvent('cartcount:update', { detail: cartResponse })
     on window; its Header registers events.register(window, 'cartcount:update').
     We mirror that exactly so the header count + flyout stay in sync. */
  async function fetchCart() {
    try {
      const res = await fetch(CART_GET(), { headers: { 'Accept': 'application/json' } });
      if (!res.ok) return null;
      const data = await res.json();
      return (data && typeof data.item_count === 'number') ? data : null;
    } catch (err) { return null; }
  }

  function broadcastCart(cart) {
    // Never dispatch with a null/garbage payload — Empire's header listener reads
    // event.detail.item_count directly and a null detail throws a TypeError, which
    // leaves the header silently stale after a SUCCESSFUL add. Bail if we don't
    // have a usable cart object.
    if (!cart || typeof cart.item_count !== 'number') return;
    try {
      window.dispatchEvent(new CustomEvent('cartcount:update', { detail: cart }));
    } catch (err) { /* no-op */ }
    // Also dispatch the OS2.0 product-form event some drawers listen for.
    try {
      document.dispatchEvent(new CustomEvent('product-form:cart:update', { bubbles: true, detail: { cart } }));
    } catch (err) { /* no-op */ }
  }

  const ADD_ICON = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><path d="m5 13 4 4L19 7"/></svg>';

  /* ── delegated handlers (bound under an AbortController, see bindDelegated) ─ */

  /* qty stepper ± */
  function onStepperClick(e) {
    const inc = e.target.closest('.afxv-grid .afxv-inc');
    const dec = e.target.closest('.afxv-grid .afxv-dec');
    if (!inc && !dec) return;
    const stepper = stepperOf(inc || dec);
    if (!stepper) return;
    const input = stepper.querySelector('[data-afxv-qty]');
    if (!input) return;
    let v = clampQty(input.value);
    v = inc ? Math.min(99, v + 1) : Math.max(1, v - 1);
    input.value = v;
    reflectQty(stepper);
  }

  /* qty manual entry */
  function onQtyChange(e) {
    const input = e.target.closest('.afxv-grid [data-afxv-qty]');
    if (!input) return;
    input.value = clampQty(input.value);
    reflectQty(stepperOf(input));
  }

  /* AJAX add */
  async function onAddClick(e) {
    const btn = e.target.closest('.afxv-grid [data-afxv-add]');
    if (!btn) return;
    e.preventDefault();
    if (btn.getAttribute('data-state')) return;

    const variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    const card = cardOf(btn);
    const pack = card ? parseInt(card.getAttribute('data-pack'), 10) || 0 : 0;
    const unitNoun = (card && card.getAttribute('data-unit-noun')) || 'unit';
    const input = card && card.querySelector('[data-afxv-qty]');
    const qty = input ? clampQty(input.value) : 1;

    // The design's quick-add (+) is an icon-only square — never inject label text
    // into it, or the word "Adding"/"Added" overflows the 38px button.
    const iconOnly = btn.hasAttribute('data-afxv-icononly');
    const originalHTML = btn.innerHTML;
    btn.setAttribute('data-state', 'adding');
    btn.setAttribute('aria-busy', 'true');
    btn.innerHTML = iconOnly
      ? '<span class="afxv-spin" aria-hidden="true"></span>'
      : '<span class="afxv-spin" aria-hidden="true"></span><span>Adding</span>';

    try {
      const addRes = await fetch(CART_ADD(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: variantId, quantity: qty })
      });
      const addData = await addRes.json();
      if (!addRes.ok) throw addData;

      // pull the authoritative cart state for the header, with one retry — a flaky
      // cart.js GET must NOT leave the header stale after a successful add.
      let cart = await fetchCart();
      if (!cart || typeof cart.item_count !== 'number') {
        cart = await fetchCart();
      }
      broadcastCart(cart);

      btn.setAttribute('data-state', 'added');
      btn.removeAttribute('aria-busy');
      btn.innerHTML = iconOnly ? ADD_ICON : `${ADD_ICON}<span>Added</span>`;
      // keep the accessible name in sync with the visible state (was "Add … to cart")
      const addedLabel = btn.getAttribute('data-label-added');
      if (addedLabel) btn.setAttribute('aria-label', addedLabel);

      const units = pack > 0 ? qty * pack : qty;
      const msg = pack > 0
        ? `Added ${qty} case${qty > 1 ? 's' : ''} · ${units} ${unitNoun}${units > 1 ? 's' : ''}`
        : `Added ${qty} to cart`;
      showToast(msg);
      announce(msg); // a11y-14: mirror the toast into the live region

      setTimeout(() => {
        btn.removeAttribute('data-state');
        btn.innerHTML = originalHTML;
        const idleLabel = btn.getAttribute('data-label-idle');
        if (idleLabel) btn.setAttribute('aria-label', idleLabel);
        announce(''); // clear the live region once the button resets
      }, 1800);
    } catch (err) {
      btn.removeAttribute('data-state');
      btn.removeAttribute('aria-busy');
      btn.innerHTML = originalHTML;
      const detail = err && (err.description || err.message);
      const errMsg = detail ? `Could not add: ${detail}` : 'Could not add to cart. Please try again.';
      showToast(errMsg);
      announce(errMsg); // a11y-14: announce the failure too
      // eslint-disable-next-line no-console
      console.error('[afxv add]', err);
    }
  }

  /* ── view toggle (grid · list) ─────────────────────────────────────────────
     Velocity exposes grid + list only (dense is hidden in CSS). The two differ by
     ONE container class (.afxv-grid--list) — pure CSS, identical markup — so the
     toggle is fully client-side: swap the class, persist in localStorage, restore
     on load. No reload, so it's instant. We do NOT round-trip through a ?grid_list
     param because Shopify Liquid's request.url carries no query string, so the
     server can't read it back anyway (that was the original toggle's bug: it
     reloaded, the server fell back to grid, and the view appeared to "revert"). */
  function currentServerView() {
    const grid = document.querySelector('[data-productgrid-items].afxv-grid');
    if (!grid) return 'grid-view';
    if (grid.classList.contains('afxv-grid--dense')) return 'dense-view';
    if (grid.classList.contains('afxv-grid--list')) return 'list-view';
    return 'grid-view';
  }

  function syncButtons(view) {
    document.querySelectorAll('[data-afxv-view]').forEach((b) => {
      const active = b.getAttribute('data-afxv-view') === view;
      b.classList.toggle('active', active);
      b.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  function gotoView(view) {
    try { localStorage.setItem(VIEW_KEY, view); } catch (err) { /* no-op */ }
    const url = new URL(window.location);
    url.searchParams.set('grid_list', view);
    window.location.assign(url.toString());
  }

  function applyGridList(view) {
    // List view is a pure CSS reflow of the SAME markup: it needs the container
    // class (single-column stack) AND a class on every card (horizontal row). Grid
    // and list cards are byte-identical except for this class, so we can swap both
    // client-side with no reload.
    const isList = view === 'list-view';
    document.querySelectorAll('[data-productgrid-items].afxv-grid').forEach((grid) => {
      grid.classList.toggle('afxv-grid--list', isList);
      grid.querySelectorAll('[data-afxv-card]').forEach((card) => {
        card.classList.toggle('afxv-card--list', isList);
      });
    });
  }

  function initView() {
    // Restore the saved view on load. Shopify Liquid's request.url carries NO query
    // string, so the server can't honor ?grid_list — grid↔list is CSS-only and lives
    // entirely client-side (persisted in localStorage). Dense is hidden in velocity.
    let stored = null;
    try { stored = localStorage.getItem(VIEW_KEY); } catch (err) { /* no-op */ }
    if (stored === 'list-view' || stored === 'grid-view') {
      applyGridList(stored);
      syncButtons(stored);
    } else {
      syncButtons(currentServerView());
    }
  }

  function onViewClick(e) {
    const btn = e.target.closest('[data-afxv-view]');
    if (!btn) return;
    e.preventDefault();
    const view = btn.getAttribute('data-afxv-view');
    if (!view) return;

    // grid↔list: CSS-only swap, no reload (server can't read the param). Persist it.
    if (view === 'grid-view' || view === 'list-view') {
      applyGridList(view);
      syncButtons(view);
      try { localStorage.setItem(VIEW_KEY, view); } catch (err) { /* no-op */ }
      return;
    }
    // dense-view (hidden in velocity) would need a server render — fall back to reload.
    syncButtons(view);
    gotoView(view);
  }

  /* ── price preset pills (sidebar) ──────────────────────────────────────────
     Each preset carries the price facet's param names + a min/max value. Because
     Empire's faceted filtering is a full reload (window.location.search = …), we
     just rewrite the price params on the current URL — preserving every other
     active filter + sort — and navigate. Empty bound = remove that param. */
  function onPresetClick(e) {
    const pill = e.target.closest('[data-afxv-price-preset]');
    if (!pill) return;
    e.preventDefault();
    const minName = pill.getAttribute('data-min-name');
    const maxName = pill.getAttribute('data-max-name');
    if (!minName || !maxName) return;
    const min = pill.getAttribute('data-min') || '';
    const max = pill.getAttribute('data-max') || '';
    const url = new URL(window.location);
    if (min) { url.searchParams.set(minName, min); } else { url.searchParams.delete(minName); }
    if (max) { url.searchParams.set(maxName, max); } else { url.searchParams.delete(maxName); }
    url.searchParams.delete('page');
    window.location.assign(url.toString());
  }

  /* ── desktop filter collapse (3-up open ↔ 4-up closed) ─────────────────────
     The design's "Filters" pill toggles the inline sidebar on desktop. We
     intercept the trigger in the CAPTURE phase so Empire's mobile-modal handler
     (bound in the bubble phase) never fires on desktop; on mobile we don't
     intercept, so Empire opens its filters drawer as usual. */
  const isDesktop = () => window.matchMedia('(min-width: 860px)').matches;
  function outerEl() {
    return document.querySelector('.collection--section .productgrid--outer')
      || document.querySelector('.productgrid--outer');
  }
  function applyCollapse(collapsed) {
    const o = outerEl();
    if (!o) return;
    o.classList.toggle('afxv-filters-collapsed', collapsed);
    document.querySelectorAll('[data-productgrid-trigger-filters]').forEach((b) => {
      b.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      b.classList.toggle('is-collapsed', collapsed);
    });
  }
  function onFilterCollapse(e) {
    const btn = e.target.closest('[data-productgrid-trigger-filters]');
    if (!btn) return;
    if (!isDesktop()) return; // mobile → Empire opens the drawer
    e.preventDefault();
    e.stopImmediatePropagation();
    const o = outerEl();
    if (!o) return;
    const collapsed = !o.classList.contains('afxv-filters-collapsed');
    applyCollapse(collapsed);
    try { localStorage.setItem(FILTERS_KEY, collapsed ? '1' : '0'); } catch (err) { /* no-op */ }
  }

  /* ── brand "Show all N" expander ───────────────────────────────────────── */
  function onShowAll(e) {
    const t = e.target.closest('[data-afxv-showall]');
    if (!t) return;
    e.preventDefault();
    const group = t.closest('[data-filter-group]');
    if (!group) return;
    const open = group.classList.toggle('afxv-showall-open');
    t.textContent = open ? (t.getAttribute('data-label-less') || 'Show less')
                         : (t.getAttribute('data-label-more') || 'Show all');
    t.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  /* ── (re)bind all delegated handlers against a fresh AbortController ─────── */
  let delegatedController = null;
  function bindDelegated() {
    if (delegatedController) delegatedController.abort();
    delegatedController = new AbortController();
    const signal = delegatedController.signal;
    document.addEventListener('click', onStepperClick, { signal });
    document.addEventListener('change', onQtyChange, { signal });
    document.addEventListener('click', onAddClick, { signal });
    document.addEventListener('click', onViewClick, { signal });
    document.addEventListener('click', onPresetClick, { signal });
    document.addEventListener('click', onFilterCollapse, { capture: true, signal });
    document.addEventListener('click', onShowAll, { signal });
  }

  /* ── boot + theme-editor re-render safety ─────────────────────────────── */
  function boot() {
    if (!document.querySelector('.afxv-grid')) return;
    initView();
    // restore the desktop filter-collapse choice (no-op on mobile). The inline
    // parse-time script (cwv-1) already applies the class pre-paint; this keeps
    // the trigger's aria-expanded in sync and covers JS-only re-renders.
    try {
      if (isDesktop() && localStorage.getItem(FILTERS_KEY) === '1') applyCollapse(true);
    } catch (err) { /* no-op */ }
    // normalise stepper button disabled states on load (dense view)
    document.querySelectorAll('.afxv-grid .afxv-stepper').forEach(reflectQty);
  }

  bindDelegated();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: re-init when the section is (re)loaded — re-bind delegated
  // handlers (the previous controller was aborted on unload) and re-run boot.
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target && e.target.querySelector && e.target.querySelector('.afxv-grid')) {
      bindDelegated();
      boot();
    }
  });

  // Theme editor: on unload, abort all delegated handlers (cwv-6 — no stale
  // capture-phase listener double-firing against Empire) and tear down the
  // floating toast so it doesn't linger as an orphan with a live timer.
  document.addEventListener('shopify:section:unload', (e) => {
    if (!e.target || !e.target.querySelector || !e.target.querySelector('.afxv-grid')) return;
    if (delegatedController) { delegatedController.abort(); delegatedController = null; }
    clearTimeout(toastTimer);
    toastTimer = null;
    if (toastNode && toastNode.parentNode) toastNode.parentNode.removeChild(toastNode);
    toastNode = null;
  });
})();
